import { useEffect, useState } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi, teamsApi } from "@/lib/api";
import type {
  AdminTeamSummary,
  TeamMember,
} from "@/lib/types";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import {
  Plus,
  Minus,
  UserPlus,
  Coins,
  Sliders,
  Users,
  X,
  Copy,
  Check,
  ShieldCheck,
  Gauge,
  KeyRound,
} from "lucide-react";
import { cn, formatSTAmount } from "@/lib/utils";
import RateLimitsModal from "./components/RateLimitsModal";
import ResetPasswordModal from "@/components/ResetPasswordModal";

type ModalState =
  | { type: "closed" }
  | { type: "create" }
  | { type: "addAdmin"; team: AdminTeamSummary }
  | { type: "recharge"; team: AdminTeamSummary }
  | { type: "quota"; team: AdminTeamSummary }
  | { type: "members"; team: AdminTeamSummary }
  | { type: "allowlist"; team: AdminTeamSummary }
  | { type: "rateLimits"; team: AdminTeamSummary };

export default function AdminTeams() {
  const { t } = useLanguage();
  const [teams, setTeams] = useState<AdminTeamSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalState>({ type: "closed" });
  const [showArchived, setShowArchived] = useState(false);

  const refresh = async () => {
    try {
      const res = await adminApi.listAllTeams({ page: 1, page_size: 1000 });
      setTeams(res.teams);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filteredTeams = teams.filter(t => showArchived ? true : t.team.status === "active");

  return (
    <ConsoleLayout
      title={t("admin.teams.title")}
      subtitle={t("admin.teams.subtitle")}
      actions={
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input 
              type="checkbox" 
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-white/20 bg-white/5"
            />
            显示已归档
          </label>
          <Button
            size="sm"
            className="btn-primary-sci gap-1.5 text-xs h-8"
            onClick={() => setModal({ type: "create" })}
          >
            <Plus size={12} />
            {t("admin.teams.newTeam")}
          </Button>
        </div>
      }
    >
      <div className="p-6 space-y-4">
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[2fr_1.5fr_1.2fr_1.2fr_80px_140px_240px] gap-3 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div>{t("admin.teams.colName")}</div>
            <div>{t("admin.teams.colOwner")}</div>
            <div>{t("admin.teams.colWallet")}</div>
            <div>{t("admin.teams.colQuota")}</div>
            <div className="text-right">{t("admin.teams.colMembers")}</div>
            <div>{t("admin.teams.colCreated")}</div>
            <div className="text-right">{t("admin.teams.colActions")}</div>
          </div>
          {loading ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">Loading…</div>
          ) : filteredTeams.length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">
              {t("admin.teams.empty")}
            </div>
          ) : (
            filteredTeams.map((ts) => (
              <TeamRow
                key={ts.team.id}
                ts={ts}
                onAddAdmin={() => setModal({ type: "addAdmin", team: ts })}
                onRecharge={() => setModal({ type: "recharge", team: ts })}
                onUpdateQuota={() => setModal({ type: "quota", team: ts })}
                onViewMembers={() => setModal({ type: "members", team: ts })}
                onAllowlist={() => setModal({ type: "allowlist", team: ts })}
                onRateLimits={() => setModal({ type: "rateLimits", team: ts })}
              />
            ))
          )}
        </div>
      </div>

      {modal.type === "create" && (
        <CreateTeamModal
          onClose={() => setModal({ type: "closed" })}
          onSaved={() => {
            setModal({ type: "closed" });
            refresh();
          }}
        />
      )}
      {modal.type === "addAdmin" && (
        <AddAdminModal
          team={modal.team}
          onClose={() => setModal({ type: "closed" })}
          onSaved={() => {
            setModal({ type: "closed" });
            refresh();
          }}
        />
      )}
      {modal.type === "recharge" && (
        <RechargeModal
          team={modal.team}
          onClose={() => setModal({ type: "closed" })}
          onSaved={() => {
            setModal({ type: "closed" });
            refresh();
          }}
        />
      )}
      {modal.type === "quota" && (
        <QuotaModal
          team={modal.team}
          onClose={() => setModal({ type: "closed" })}
          onSaved={() => {
            setModal({ type: "closed" });
            refresh();
          }}
        />
      )}
      {modal.type === "members" && (
        <MembersModal
          team={modal.team}
          onClose={() => setModal({ type: "closed" })}
        />
      )}
      {modal.type === "allowlist" && (
        <AllowlistModal
          team={modal.team}
          onClose={() => setModal({ type: "closed" })}
          onSaved={() => {
            setModal({ type: "closed" });
            refresh();
          }}
        />
      )}
      {modal.type === "rateLimits" && (
        <RateLimitsModal
          team={modal.team}
          onClose={() => setModal({ type: "closed" })}
        />
      )}
    </ConsoleLayout>
  );
}

function TeamRow({
  ts,
  onAddAdmin,
  onRecharge,
  onUpdateQuota,
  onViewMembers,
  onAllowlist,
  onRateLimits,
}: {
  ts: AdminTeamSummary;
  onAddAdmin: () => void;
  onRecharge: () => void;
  onUpdateQuota: () => void;
  onViewMembers: () => void;
  onAllowlist: () => void;
  onRateLimits: () => void;
}) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const st = Number(ts.wallet?.st_balance ?? 0);
  const maxMembers = ts.quota?.max_members;

  const copyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(ts.team.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="px-5 py-4 border-b border-white/5 last:border-0 grid grid-cols-[2fr_1.5fr_1.2fr_1.2fr_80px_140px_240px] gap-3 items-center hover:bg-white/3 transition-colors">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-white truncate">{ts.team.name}</div>
          {ts.team.status === "archived" ? (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-500/20 text-slate-400">已归档</span>
          ) : (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/20 text-emerald-400">active</span>
          )}
        </div>
        <button
          onClick={copyId}
          className="text-[10px] font-mono text-slate-500 hover:text-slate-300 flex items-center gap-1"
          title="Copy team ID"
        >
          <span className="truncate max-w-[200px]">{ts.team.id}</span>
          {copied ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
        </button>
      </div>
      <div className="min-w-0">
        {ts.owner ? (
          <>
            <div className="text-xs text-slate-300 truncate">{ts.owner.display_name}</div>
            <div className="text-[10px] text-slate-500 truncate">{ts.owner.email}</div>
          </>
        ) : (
          <span className="text-xs text-slate-600">{t("admin.teams.noOwner")}</span>
        )}
      </div>
      <div>
        <div className="text-xs font-mono text-emerald-400">{formatSTAmount(st)} ST</div>
      </div>
      <div className="text-[10px] text-slate-400 font-mono space-y-0.5">
        <div className="text-slate-500">-</div>
      </div>
      <div className="text-xs text-slate-300 text-right font-mono">
        {ts.member_count} / {maxMembers != null ? maxMembers : "∞"}
      </div>
      <div className="text-[10px] text-slate-500">
        {new Date(ts.team.created_at).toLocaleDateString()}
      </div>
      <div className="flex items-center gap-1 justify-end">
        {ts.team.status !== "archived" && (
          <IconButton title="配置允许供应商" onClick={onAllowlist}>
            <ShieldCheck size={13} />
          </IconButton>
        )}
        <IconButton title="配置模型限速" onClick={onRateLimits}>
          <Gauge size={13} />
        </IconButton>
        <IconButton title={t("admin.teams.addAdmin")} onClick={onAddAdmin}>
          <UserPlus size={13} />
        </IconButton>
        <IconButton title={t("admin.teams.recharge")} onClick={onRecharge}>
          <Coins size={13} />
        </IconButton>
        <IconButton title={t("admin.teams.updateQuota")} onClick={onUpdateQuota}>
          <Sliders size={13} />
        </IconButton>
        <IconButton title={t("admin.teams.viewMembers")} onClick={onViewMembers}>
          <Users size={13} />
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({
  children,
  title,
  onClick,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
    >
      {children}
    </button>
  );
}

// ─── Modals ───

function ModalShell({
  title,
  onClose,
  children,
  maxWidth = "max-w-[480px]",
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={cn("relative w-full mx-4 sci-card p-6 max-h-[90vh] overflow-y-auto", maxWidth)}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CreateTeamModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const n = name.trim();
    if (!n) {
      toast.error(t("admin.teams.name") + " " + t("admin.common.required"));
      return;
    }
    setBusy(true);
    try {
      await adminApi.createTeam({ name: n });
      toast.success("Team created");
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={t("admin.teams.modalNewTitle")} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">
            {t("admin.teams.name")}
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Research Team A"
            className="bg-white/5 border-white/10 text-white text-sm"
            autoFocus
          />
        </div>
      </div>
      <div className="flex gap-3 pt-5">
        <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
          {t("admin.common.cancel")}
        </Button>
        <Button onClick={submit} disabled={busy} className="flex-1 btn-primary-sci">
          {busy ? "..." : t("admin.common.create")}
        </Button>
      </div>
    </ModalShell>
  );
}

function AddAdminModal({
  team,
  onClose,
  onSaved,
}: {
  team: AdminTeamSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);
  const [pwdCopied, setPwdCopied] = useState(false);

  const submit = async () => {
    const e = email.trim();
    if (!e) {
      toast.error(t("admin.teams.email") + " " + t("admin.common.required"));
      return;
    }
    setBusy(true);
    try {
      const res = await adminApi.addTeamAdmin(team.team.id, { email: e });
      if (res.initial_password) {
        setCreated({
          name: res.user.display_name,
          email: res.user.email,
          password: res.initial_password,
        });
      } else {
        toast.success(`${res.user.display_name} → team_admin`);
        onSaved();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Add admin failed");
    } finally {
      setBusy(false);
    }
  };

  const copyPwd = () => {
    if (!created) return;
    navigator.clipboard.writeText(created.password);
    setPwdCopied(true);
    setTimeout(() => setPwdCopied(false), 2000);
  };

  const finishAndClose = () => {
    onSaved();
  };

  if (created) {
    return (
      <ModalShell
        title={`${t("admin.teams.modalAddAdminTitle")} — ${team.team.name}`}
        onClose={finishAndClose}
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
            初始密码仅显示一次，请立即复制并安全发送给该用户。<br />
            <span className="text-amber-300/80">
              This password is shown only once. Copy it now and send it to the user securely.
            </span>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-slate-500 w-20">User</span>
              <span className="text-white">{created.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 w-20">Email</span>
              <span className="text-white font-mono">{created.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 w-20">Password</span>
              <code className="flex-1 px-2 py-1.5 rounded bg-black/40 border border-white/10 text-emerald-300 font-mono text-sm select-all">
                {created.password}
              </code>
              <button
                onClick={copyPwd}
                title="Copy password"
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
              >
                {pwdCopied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              </button>
            </div>
            <p className="text-[10px] text-slate-500 pt-2">
              用户首次登录会被强制修改密码 · The user must change this password on first login.
            </p>
          </div>
        </div>
        <div className="flex gap-3 pt-5">
          <Button onClick={finishAndClose} className="flex-1 btn-primary-sci">
            完成 · Done
          </Button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title={`${t("admin.teams.modalAddAdminTitle")} — ${team.team.name}`}
      onClose={onClose}
    >
      <p className="text-xs text-slate-400 leading-relaxed mb-4">
        {t("admin.teams.modalAddAdminHint")}
      </p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">
            {t("admin.teams.email")}
          </Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alice@example.com"
            className="bg-white/5 border-white/10 text-white text-sm"
            autoFocus
          />
          <p className="text-[10px] text-slate-500 mt-1.5">
            若该邮箱未注册，将自动创建用户并生成初始密码 · If unregistered, a new user will be created with a generated password.
          </p>
        </div>
      </div>
      <div className="flex gap-3 pt-5">
        <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
          {t("admin.common.cancel")}
        </Button>
        <Button onClick={submit} disabled={busy} className="flex-1 btn-primary-sci">
          {busy ? "..." : t("admin.teams.addAdmin")}
        </Button>
      </div>
    </ModalShell>
  );
}

function RechargeModal({
  team,
  onClose,
  onSaved,
}: {
  team: AdminTeamSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const [opType, setOpType] = useState<"recharge" | "deduct">("recharge");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    const signedAmount = opType === "deduct" ? `-${amount}` : amount;
    setBusy(true);
    try {
      const res = await adminApi.recharge(team.team.id, {
        amount: signedAmount,
        note: note.trim() || undefined,
      });
      toast.success(
        `${t(
          opType === "deduct"
            ? "admin.teams.deductedToast"
            : "admin.teams.rechargedToast"
        )} — ${formatSTAmount(res.st_balance)} ST`
      );
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Operation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`${t("admin.teams.modalRechargeTitle")} — ${team.team.name}`}
      onClose={onClose}
    >
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">
            {t("admin.teams.opType")}
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setOpType("recharge")}
              className={cn(
                "flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium border transition-colors",
                opType === "recharge"
                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                  : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20"
              )}
            >
              <Plus size={13} />
              {t("admin.teams.opTypeRecharge")}
            </button>
            <button
              type="button"
              onClick={() => setOpType("deduct")}
              className={cn(
                "flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium border transition-colors",
                opType === "deduct"
                  ? "bg-red-500/15 border-red-500/40 text-red-300"
                  : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20"
              )}
            >
              <Minus size={13} />
              {t("admin.teams.opTypeDeduct")}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">
            {t("admin.teams.amount")}
          </Label>
          <Input
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="10000"
            className="bg-white/5 border-white/10 text-white text-sm"
            autoFocus
          />
          <div className="text-[10px] text-slate-500 mt-1">
            Current: {formatSTAmount(team.wallet?.st_balance ?? 0)} ST
          </div>
          {opType === "deduct" && (
            <div className="text-[10px] text-amber-400/80 mt-1">
              {t("admin.teams.deductHint")}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">
            {t("admin.teams.note")}
          </Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. monthly top-up"
            className="bg-white/5 border-white/10 text-white text-sm"
          />
        </div>
      </div>
      <div className="flex gap-3 pt-5">
        <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
          {t("admin.common.cancel")}
        </Button>
        <Button
          onClick={submit}
          disabled={busy}
          className={cn(
            "flex-1",
            opType === "deduct"
              ? "bg-red-600 hover:bg-red-500 text-white"
              : "btn-primary-sci"
          )}
        >
          {busy
            ? "..."
            : t(
                opType === "deduct"
                  ? "admin.teams.opTypeDeduct"
                  : "admin.teams.opTypeRecharge"
              )}
        </Button>
      </div>
    </ModalShell>
  );
}

function QuotaModal({
  team,
  onClose,
  onSaved,
}: {
  team: AdminTeamSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const [maxMembers, setMaxMembers] = useState(
    team.quota?.max_members?.toString() ?? "",
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const payload: {
      max_members?: number | null;
    } = {};
    const ml = maxMembers.trim();
    
    if (ml === "") payload.max_members = null;
    else {
      const n = Number(ml);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1)
        return toast.error("Invalid member limit (must be a positive integer)");
      payload.max_members = n;
    }
    setBusy(true);
    try {
      await adminApi.updateQuota(team.team.id, payload);
      toast.success("Quota updated");
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`${t("admin.teams.modalQuotaTitle")} — ${team.team.name}`}
      onClose={onClose}
    >
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">
            {t("admin.teams.memberLimit")}{" "}
            <span className="text-slate-600">{t("admin.teams.unlimited")}</span>
          </Label>
          <Input
            type="number"
            min="1"
            step="1"
            value={maxMembers}
            onChange={(e) => setMaxMembers(e.target.value)}
            className="bg-white/5 border-white/10 text-white text-sm"
          />
        </div>
      </div>
      <div className="flex gap-3 pt-5">
        <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
          {t("admin.common.cancel")}
        </Button>
        <Button onClick={submit} disabled={busy} className="flex-1 btn-primary-sci">
          {busy ? "..." : t("admin.common.update")}
        </Button>
      </div>
    </ModalShell>
  );
}

function MembersModal({
  team,
  onClose,
}: {
  team: AdminTeamSummary;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const { user: me } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<TeamMember | null>(null);

  const load = () => {
    setLoading(true);
    teamsApi
      .listMembers(team.team.id, { includeInactive: true })
      .then((res) => setMembers(res.members))
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Failed to load members");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team.team.id]);

  const toggleStatus = async (m: TeamMember) => {
    const next: "active" | "inactive" = m.status === "active" ? "inactive" : "active";

    if (next === "inactive" && m.is_system_admin) {
      const ok = window.confirm(
        `确定要停用系统管理员 ${m.display_name} (${m.email}) 吗？\n\n` +
          `Deactivate system admin ${m.display_name} (${m.email})? They will lose all access until reactivated.`,
      );
      if (!ok) return;
    }

    setBusyId(m.id);
    try {
      await teamsApi.setMemberStatus(team.team.id, m.id, next);
      setMembers((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, status: next } : x)),
      );
      toast.success(
        next === "active"
          ? `${m.display_name} 已激活 · Activated`
          : `${m.display_name} 已停用 · Deactivated`,
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ModalShell
      title={`${t("admin.teams.modalMembersTitle")} — ${team.team.name}`}
      onClose={onClose}
      maxWidth="max-w-[640px]"
    >
      {loading ? (
        <div className="text-xs text-slate-500 text-center py-6">Loading…</div>
      ) : members.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-6">
          {t("admin.teams.noMembers")}
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {members.map((m) => {
            const isActive = m.status === "active";
            const isSelf = me?.id === m.id;
            return (
              <div
                key={m.id}
                className={cn(
                  "py-3 flex items-center justify-between gap-3",
                  !isActive && "opacity-60",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate flex items-center gap-2">
                    {m.display_name}
                    {isSelf && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-400 font-mono">
                        you
                      </span>
                    )}
                    {m.is_system_admin && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 font-mono">
                        sysadmin
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">{m.email}</div>
                </div>
                <span
                  className={cn(
                    "text-[10px] px-2 py-0.5 rounded-full font-mono flex-shrink-0",
                    m.role === "team_admin"
                      ? "bg-blue-500/15 text-blue-400"
                      : "bg-slate-500/15 text-slate-400",
                  )}
                >
                  {m.role}
                </span>
                <span
                  className={cn(
                    "text-[10px] px-2 py-0.5 rounded-full font-mono flex-shrink-0",
                    isActive
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-slate-500/15 text-slate-500",
                  )}
                >
                  {isActive ? "active" : "inactive"}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busyId === m.id || isSelf || !isActive}
                  onClick={() => setResetTarget(m)}
                  className="text-[10px] h-7 px-2.5 border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 gap-1 flex-shrink-0"
                  title={
                    isSelf
                      ? t("teamMgmt.cannotSelf")
                      : !isActive
                      ? t("teamMgmt.deactivated")
                      : t("teamMgmt.resetPwd")
                  }
                >
                  <KeyRound size={11} />
                  {t("teamMgmt.resetPwd")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busyId === m.id || isSelf}
                  onClick={() => toggleStatus(m)}
                  className={cn(
                    "text-[10px] h-7 px-2.5 border border-white/10",
                    isActive
                      ? "text-amber-300 hover:text-amber-200 hover:bg-amber-500/10"
                      : "text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10",
                  )}
                  title={isSelf ? "Cannot deactivate yourself" : undefined}
                >
                  {busyId === m.id
                    ? "..."
                    : isActive
                      ? "停用 · Deactivate"
                      : "激活 · Activate"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <div className="pt-5">
        <Button variant="ghost" onClick={onClose} className="w-full text-slate-400 hover:text-white border border-white/10">
          {t("admin.common.cancel")}
        </Button>
      </div>
      {resetTarget && (
        <ResetPasswordModal
          teamId={team.team.id}
          member={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
    </ModalShell>
  );
}

function AllowlistModal({
  team,
  onClose,
  onSaved,
}: {
  team: AdminTeamSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [providers, setProviders] = useState<import("@/lib/types").Provider[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      adminApi.getProviders(),
      adminApi.getTeamAllowedProviders(team.team.id),
    ])
      .then(([all, allowed]) => {
        setProviders(all.filter(p => p.is_active));
        setSelectedIds(new Set(allowed.map((p: any) => p.id)));
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Failed to load providers");
      })
      .finally(() => setLoading(false));
  }, [team.team.id]);

  const toggle = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.setTeamAllowedProviders(team.team.id, Array.from(selectedIds));
      toast.success("供应商白名单已更新");
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`配置允许供应商 — ${team.team.name}`}
      onClose={onClose}
      maxWidth="max-w-[480px]"
    >
      {loading ? (
        <div className="text-xs text-slate-500 text-center py-6">Loading…</div>
      ) : (
        <div className="space-y-4">
          {providers.length === 0 ? (
            <div className="text-xs text-slate-500 text-center">暂无可用供应商</div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {providers.map(p => (
                <label key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-white/5 bg-white/5 hover:bg-white/10 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onChange={() => toggle(p.id)}
                    className="w-4 h-4 rounded border-white/20 bg-black/40 text-emerald-500 focus:ring-emerald-500/20 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.slug}</div>
                  </div>
                </label>
              ))}
            </div>
          )}
          {selectedIds.size === 0 && providers.length > 0 && (
            <div className="text-[10px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2 text-center">
              清空后该团队将看不到任何模型
            </div>
          )}
        </div>
      )}
      <div className="flex gap-3 pt-5 mt-2 border-t border-white/5">
        <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
          取消
        </Button>
        <Button onClick={submit} disabled={busy || loading} className="flex-1 btn-primary-sci">
          {busy ? "..." : "保存"}
        </Button>
      </div>
    </ModalShell>
  );
}
